import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

const TermsAndConditions = () => {
  const generalTerms = [
    {
      icon: "Shield",
      title: "Términos Generales",
      description: "Estos términos y condiciones rigen el uso de los servicios proporcionados por BIZZNET, una plataforma que conecta empresas y clientes para ofrecer descuentos exclusivos. Al acceder y utilizar nuestros servicios, aceptas cumplir con los siguientes términos y condiciones."
    },
    {
      icon: "CheckCircle2",
      title: "Uso Autorizado",
      description: "Los servicios de BIZZNET están destinados exclusivamente a empresas registradas y clientes que deseen acceder a descuentos en las tiendas participantes."
    }
  ];

  const registrationTerms = [
    {
      icon: "Building2",
      title: "Registro de Empresas",
      description: "Las empresas que deseen ofrecer descuentos a través de nuestra plataforma deberán registrarse y proporcionar información precisa sobre sus productos, precios y condiciones."
    },
    {
      icon: "Users",
      title: "Registro de Clientes",
      description: "Los clientes deben registrarse en la plataforma y aceptar nuestras políticas para acceder a los descuentos disponibles en las tiendas asociadas."
    },
    {
      icon: "Scale",
      title: "Responsabilidad",
      description: "Ambas partes (empresas y clientes) son responsables de la veracidad de la información proporcionada y deben asegurarse de que sus acciones en la plataforma cumplan con las leyes locales."
    }
  ];

  const businessConditions = [
    {
      icon: "FileCheck",
      title: "Registro",
      description: "Las empresas se comprometen a registrar información clara y verídica de su negocio. BIZZNET no se responsabiliza por falsa información o información engañosa."
    },
    {
      icon: "Tag",
      title: "Ofertas y Descuentos",
      description: "Debes ofrecer las ofertas y descuentos de manera clara y precisa. Cualquier cambio en las ofertas debe ser comunicado a los clientes a través de la plataforma."
    },
    {
      icon: "Megaphone",
      title: "Promoción",
      description: "BIZZNET no se hace responsable de la promoción o publicidad fuera de la plataforma. Las empresas deben gestionar sus propias campañas de marketing."
    }
  ];

  const clientConditions = [
    {
      icon: "Percent",
      title: "Acceso a Descuentos",
      description: "Los clientes podrán acceder a descuentos exclusivos al registrar su cuenta y utilizar la plataforma para escanear códigos QR en las tiendas asociadas."
    },
    {
      icon: "CreditCard",
      title: "Renovar Membresía",
      description: "Los clientes deben renovar su membresía para poder utilizar los descuentos ofrecidos. BIZZNET no se responsabiliza por compras no realizadas o no aplicadas."
    },
    {
      icon: "UserCheck",
      title: "Comportamiento",
      description: "Los clientes deben hacer un uso adecuado y respetuoso de la plataforma. Cualquier uso indebido de la misma podrá llevar a la suspensión o eliminación de la cuenta."
    }
  ];

  const privacyPolicies = [
    {
      icon: "Database",
      title: "Recopilación de Datos",
      description: "Recopilamos información personal como nombre, correo electrónico y preferencias de compra para mejorar la experiencia del usuario y personalizar las ofertas de descuento."
    },
    {
      icon: "Lock",
      title: "Protección de Datos",
      description: "Implementamos medidas de seguridad para proteger tus datos personales. No compartiremos tu información sin tu expreso consentimiento, excepto cuando sea necesario para procesar transacciones de pago o en cumplimiento con la ley."
    },
    {
      icon: "Key",
      title: "Derechos del Usuario",
      description: "Los usuarios pueden acceder, modificar o eliminar su información personal en cualquier momento a través de su cuenta en la plataforma."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Términos y Condiciones - BIZZNET</title>
        <meta name="description" content="Términos y condiciones de uso de BIZZNET. Conoce las políticas que rigen nuestra plataforma de descuentos exclusivos." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section - FIXED COLOR CONTRAST */}
        <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-white/90 hover:text-white transition-colors drop-shadow-md">
                <Icon name="ArrowLeft" size={24} />
              </Link>
              <h1 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
                Términos y Condiciones
              </h1>
            </div>
            <p className="text-xl md:text-2xl font-medium max-w-3xl text-white/95 drop-shadow-md">
              Es importante que leas nuestros términos y condiciones para garantizar el uso adecuado de nuestros servicios
            </p>
          </div>
        </section>

        {/* General Terms Section */}
        <section className="py-12 px-6 bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="space-y-6">
              {generalTerms?.map((term, index) => (
                <div key={index} className="bg-background border border-border rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-3 rounded-xl flex-shrink-0">
                      <Icon name={term?.icon} size={28} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-3">{term?.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{term?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Registration Terms */}
        <section className="py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Icon name="UserPlus" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Registro y Responsabilidad</h2>
            </div>
            <div className="space-y-6">
              {registrationTerms?.map((term, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-3 rounded-xl flex-shrink-0">
                      <Icon name={term?.icon} size={28} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-foreground mb-2">{term?.title}</h4>
                      <p className="text-muted-foreground leading-relaxed">{term?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Business Conditions */}
        <section className="py-12 px-6 bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Icon name="Store" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Condiciones para Empresas</h2>
            </div>
            <p className="text-muted-foreground mb-8 text-lg">
              Si eres una empresa que desea participar en la plataforma de BIZZNET y ofrecer descuentos a los clientes, debes cumplir con las siguientes condiciones:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {businessConditions?.map((condition, index) => (
                <div key={index} className="bg-background border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                    <Icon name={condition?.icon} size={28} className="text-primary" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-3">{condition?.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{condition?.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Client Conditions */}
        <section className="py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Icon name="User" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Condiciones para Clientes</h2>
            </div>
            <p className="text-muted-foreground mb-8 text-lg">
              Como cliente de BIZZNET, debes tener en cuenta lo siguiente:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {clientConditions?.map((condition, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                    <Icon name={condition?.icon} size={28} className="text-primary" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-3">{condition?.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{condition?.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Privacy Policies */}
        <section className="py-12 px-6 bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Icon name="ShieldCheck" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Políticas de Privacidad</h2>
            </div>
            <p className="text-muted-foreground mb-8 text-lg">
              Conoce cómo protegemos tus datos personales y cómo los utilizamos de manera responsable:
            </p>
            <div className="space-y-6">
              {privacyPolicies?.map((policy, index) => (
                <div key={index} className="bg-background border border-border rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-3 rounded-xl flex-shrink-0">
                      <Icon name={policy?.icon} size={28} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-foreground mb-2">{policy?.title}</h4>
                      <p className="text-muted-foreground leading-relaxed">{policy?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Modification and Acceptance */}
        <section className="py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Modification */}
              <div className="bg-card border border-border rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 p-3 rounded-xl">
                    <Icon name="RefreshCw" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">Modificación de Términos</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  BIZZNET se reserva el derecho de modificar estos términos y condiciones en cualquier momento. Cualquier cambio será publicado en este sitio y entrará en vigor inmediatamente después de su publicación. Te recomendamos revisar regularmente estos términos para estar al tanto de cualquier modificación.
                </p>
              </div>

              {/* Acceptance */}
              <div className="bg-card border border-border rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 p-3 rounded-xl">
                    <Icon name="CheckCircle" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">Aceptación de los Términos</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Al utilizar nuestros servicios, confirmas que has leído, entendido y aceptas estos términos y condiciones. Si no estás de acuerdo con alguna de las condiciones, por favor, abstente de utilizar nuestra plataforma.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - ALSO FIXED FOR CONSISTENCY */}
        <section className="py-16 px-6 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
          <div className="max-w-4xl mx-auto text-center">
            <Icon name="FileText" size={48} className="mx-auto mb-6 text-white drop-shadow-lg" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white drop-shadow-lg">
              ¿Tienes Preguntas?
            </h2>
            <p className="text-lg mb-8 text-white/95 drop-shadow-md">
              Si tienes alguna duda sobre nuestros términos y condiciones, no dudes en contactarnos
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/faq"
                className="bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2 shadow-lg"
              >
                <Icon name="HelpCircle" size={20} />
                Preguntas Frecuentes
              </Link>
              <Link
                to="/"
                className="bg-white/10 text-white border-2 border-white/30 px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-colors inline-flex items-center justify-center gap-2 backdrop-blur-sm"
              >
                <Icon name="Home" size={20} />
                Volver al Inicio
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TermsAndConditions;